import Box from "@mui/material/Box";
import type {ColumnDef} from "@tanstack/react-table";
import {ImportLog} from "crqlar-react-common/dist/models/integrations/importLog.model";
import {ImportLogService} from "crqlar-react-common/dist/services/integrations/importLog.service";
import {formatDateDMYHM, formatFullDateTime} from "crqlar-react-common/dist/utils/date.util";
import {useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {
    ServerTable,
    ServerTableColumns,
    ServerTableFrame,
    ServerTableFullscreen,
    ServerTableProvider,
    ServerTableSort,
    ServerTableToolbar,
    useServerTable
} from "../../../common/serverTable";
import ImportLogPreviewDialog from "../../common/ImportLogPreviewDialog";

interface Props {
    configId?: string
    tableId: string
}

const ImportLogsTable = ({configId, tableId}: Props) => {
    const {t} = useTranslation();
    const [selectedLog, setSelectedLog] = useState<ImportLog>();
    const columns = useMemo<ColumnDef<ImportLog>[]>(() => [
        {
            id: 'contentCategory',
            header: t('Content category'),
            accessorFn: log => log.contentCategory,
            cell: ({row}) => <Box>{row.original.contentCategory || '-'}</Box>,
            size: 240,
            meta: {defaultActive: true, isServerSortable: true, grow: true}
        },
        {
            id: 'status',
            header: t('Status'),
            accessorFn: log => log.status,
            cell: ({row}) => <Box>{row.original.status || '-'}</Box>,
            size: 140,
            meta: {defaultActive: true, isServerSortable: true}
        },
        {
            id: 'processingStart',
            header: t('Processing start'),
            accessorFn: log => log.processingStart,
            cell: ({row}) => <Box>{formatDateDMYHM(row.original.processingStart) || '-'}</Box>,
            size: 200,
            meta: {defaultActive: true, isServerSortable: true}
        },
        {
            id: 'processingDone',
            header: t('Processing done'),
            accessorFn: log => log.processingDone,
            cell: ({row}) => <Box>{formatDateDMYHM(row.original.processingDone) || '-'}</Box>,
            size: 200,
            meta: {defaultActive: true, isServerSortable: true}
        },
        {
            id: 'id',
            header: t('ID'),
            accessorFn: log => log.id,
            cell: ({row}) => <Box>{row.original.id}</Box>,
            size: 300,
            meta: {defaultActive: false, isServerSortable: true}
        },
        {
            id: 'updateDate',
            header: t('Last updated'),
            accessorFn: log => log.updateDate,
            cell: ({row}) => <Box>{formatFullDateTime(row.original.updateDate)}</Box>,
            size: 200,
            meta: {defaultActive: true, isServerSortable: true}
        }
    ], [t]);
    const controller = useServerTable({
        tableId,
        columns,
        getRowId: log => log.id!,
        enabled: !!configId,
        queryDependencies: [configId],
        initialSorting: [{id: 'processingStart', desc: true}],
        onRowClick: setSelectedLog,
        queryFn: async ({pagination, sorting}) => {
            const page = await ImportLogService.getAll({
                configIds: [configId!],
                limit: {
                    offset: pagination.pageIndex * pagination.pageSize,
                    limit: pagination.pageSize,
                    sort: sorting ? [sorting] : undefined
                }
            });

            return {
                content: page.content ?? [],
                totalElements: page.totalElements ?? 0
            };
        }
    });

    return (
        <Box sx={{display: 'flex', flexDirection: 'column', width: '100%', height: '70vh', minHeight: 0}}>
            <ServerTableProvider controller={controller}>
                <ServerTableFrame>
                    <ServerTableToolbar>
                        <ServerTableSort/>
                        <ServerTableFullscreen/>

                        <ServerTableColumns/>
                    </ServerTableToolbar>

                    <ServerTable/>
                </ServerTableFrame>
            </ServerTableProvider>

            <ImportLogPreviewDialog
                open={!!selectedLog}
                importLogId={selectedLog?.id}
                onClose={() => setSelectedLog(undefined)}
            />
        </Box>
    );
};

export default ImportLogsTable;
